import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WorkAuthenticationV2Service } from '@work-frontend-monorepo/work-rest-api';
import { Store } from '@ngxs/store';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import * as Sentry from '@sentry/angular';

@Injectable()
export class ApiTokenInterceptor implements HttpInterceptor {
  private workAuthenticationV2Service = inject(WorkAuthenticationV2Service);
  private store = inject(Store);
  private router = inject(Router);
  private alertDialogService = inject(AlertDialogService);

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Wenn die API nicht erreichbar ist (Neustart, Netzwerkunterbruch), schlagen typischerweise alle
   * gleichzeitig offenen Requests fehl. Ohne Deduplizierung würde pro fehlgeschlagenem Request ein
   * eigener Alert erscheinen (~20 identische Meldungen). Deshalb wird pro Ausfall nur ein Alert
   * angezeigt; der Merker wird zurückgesetzt, sobald wieder ein Request erfolgreich ist.
   */
  private static readonly ApiUnavailableStatusCodes = [-1, 0, 502, 503, 504];
  private static readonly ApiUnavailableAlertLifeTimeInMs = 20000;
  private apiUnavailableAlertShown = false;

  /**
   * Intercepts every HTTP request. If an access token exists, it will be attached to the request as Authorization Header
   * in the format of "Bearer ${Token}". If a http 401 exception occurs, it will attempt to get new tokens via refresh token
   * mechanism. If this fails, the user will be automatically logged out, because the refresh token is invalid or expired.
   *
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      withCredentials: true,
    });

    return next.handle(request).pipe(
      tap((event) => {
        /**
         * Sobald die API wieder antwortet, ist der Ausfall vorbei: ein späterer Ausfall soll die
         * Meldung erneut anzeigen können.
         */
        if (event instanceof HttpResponse) {
          this.apiUnavailableAlertShown = false;
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && this.store.selectSnapshot(ProfileState.profile)) {
          /**
           * Logout the user if the 401 error stems from refresh token endpoints (=> refresh token has expired)
           */
          if (request.url.indexOf(WorkAuthenticationV2Service.AuthenticationV2ControllerRefreshTokenPath) > -1) {
            // Logout
            this.router.navigateByUrl('/auth/logout').then();
            this.alertDialogService.showAlert({
              title: 'Abgemeldet',
              message: 'Aufgrund zu langer Inaktivität wurdest du automatisch abgemeldet.',
              feedbackState: 'warning',
            });
          }
          return this.handle401Error(request, next);
        } else if (error instanceof HttpErrorResponse && ApiTokenInterceptor.ApiUnavailableStatusCodes.includes(error.status)) {
          this.showApiUnavailableAlertOnce();
          throw error;
        } else if (error instanceof HttpErrorResponse && error.status !== 401) {
          Sentry.captureException(error, {
            tags: {
              url: request.url,
              method: request.method,
              status: error.status.toString(),
            },
            extra: {
              requestBody: request.body,
              response: error.error,
            },
            level: 'error',
          });
          throw error;
        } else {
          throw error;
        }
      }),
    );
  }

  /**
   * Zeigt die "API wird neu gestartet"-Meldung nur einmal pro Ausfall an, auch wenn dabei viele
   * Requests gleichzeitig fehlschlagen.
   */
  private showApiUnavailableAlertOnce(): void {
    if (this.apiUnavailableAlertShown) {
      return;
    }

    try {
      this.alertDialogService.showAlert({
        title: 'API wird neu gestartet',
        message: 'Bitte in 30 Sekunden erneut versuchen oder die Seite neu laden.',
        feedbackState: 'warning',
        alertLifeTimeInMs: ApiTokenInterceptor.ApiUnavailableAlertLifeTimeInMs,
      });
      this.apiUnavailableAlertShown = true;
    } catch {
      /**
       * Der Alert-Host existiert erst, wenn die App gerendert hat. Ist die API bereits beim Start nicht
       * erreichbar, wirft showAlert() — dann bleibt der Merker offen, damit der nächste fehlgeschlagene
       * Request die Meldung erneut versucht. Der ursprüngliche HTTP-Fehler darf dadurch nicht verloren
       * gehen und wird vom Aufrufer weitergeworfen.
       */
    }
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(false);

      /**
       * Get new access_token and refresh_token based on the current refresh_token.
       */
      return this.workAuthenticationV2Service.authenticationV2ControllerRefreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(true);
          return next.handle(request);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          return throwError(() => error);
        }),
      );
    } else {
      // Warte bis Token-Refresh abgeschlossen ist, dann wiederhole den Request
      return this.refreshTokenSubject.pipe(
        filter((tokenRefreshed) => tokenRefreshed),
        take(1),
        switchMap(() => {
          return next.handle(request);
        }),
      );
    }
  }
}
